export interface PagerProps {
  page: number;
  totalPages: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
}

// Build the numeric page list with ellipses. Always shows first, last,
// current ± 1, with "…" inserted where there's a gap of 2+ pages.
function buildPages(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out: (number | 'ellipsis')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('ellipsis');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('ellipsis');
  out.push(total);
  return out;
}

// Pill-pager per Search+ design `.pager`. Renders a top hairline,
// "Showing N-M of T" label, numeric page pills with ellipses, and
// rounded Prev/Next nav buttons.
export function Pager({ page, totalPages, perPage, total, onPageChange }: PagerProps) {
  const start = perPage * (page - 1) + 1;
  const end = Math.min(perPage * page, total);
  const pages = buildPages(page, totalPages);

  return (
    <div className="pager">
      <div className="pager-meta">Showing {start}-{end} of {total}</div>
      <div className="pager-nav">
        <button
          type="button"
          className="nav-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          ← Prev
        </button>
        <div className="pages">
          {pages.map((p, i) =>
            p === 'ellipsis' ? (
              <span key={`e-${i}`} className="pager-ellipsis" aria-hidden="true">…</span>
            ) : (
              <button
                key={p}
                type="button"
                className={p === page ? 'active' : undefined}
                aria-current={p === page ? 'page' : undefined}
                aria-label={`Page ${p}`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          className="nav-btn"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
